import { useState, useEffect, useRef } from "react";

function isHeadingStyle(style) {
  return style === "h1" || style === "h2" || style === "h3" || style === "h4";
}

function blockText(block) {
  if (!block || !Array.isArray(block.children)) return "";
  return block.children.map((c) => c.text || "").join("");
}

export function deriveStepMeta(step, index) {
  const blocks = Array.isArray(step?.body) ? step.body : [];

  let firstHeadingIdx = -1;
  let firstImageIdx = -1;
  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i];
    if (
      firstHeadingIdx === -1 &&
      b._type === "block" &&
      isHeadingStyle(b.style)
    ) {
      firstHeadingIdx = i;
    }
    if (firstImageIdx === -1 && b._type === "image") {
      firstImageIdx = i;
    }
    if (firstHeadingIdx !== -1 && firstImageIdx !== -1) break;
  }

  const headingText =
    firstHeadingIdx !== -1 ? blockText(blocks[firstHeadingIdx]) : "";
  const chapter =
    (step?.internalTitle && step.internalTitle.trim()) ||
    headingText ||
    `Chapter ${index + 1}`;
  const heading = headingText || step?.internalTitle || chapter;

  const focalImage = firstImageIdx !== -1 ? blocks[firstImageIdx] : null;

  // Strip first heading + first focal image from body flow
  const cleanedBody = blocks.filter(
    (_, i) => i !== firstHeadingIdx && i !== firstImageIdx,
  );

  return {
    chapter,
    heading,
    focalImage,
    cleanedBody,
    minutes: estimateMinutes(blocks),
  };
}

export function estimateMinutes(blocks) {
  if (!Array.isArray(blocks)) return 1;
  let words = 0;
  for (const b of blocks) {
    if (b?._type === "block") {
      const t = blockText(b);
      if (t) words += t.split(/\s+/).filter(Boolean).length;
    }
  }
  return Math.max(1, Math.round(words / 220));
}

/** localStorage-backed state. Reads once on mount, writes on change. */
export function useLocalState(key, initial) {
  const [v, setV] = useState(() => {
    if (typeof window === "undefined") return initial;
    try {
      const raw = window.localStorage.getItem(key);
      return raw == null ? initial : JSON.parse(raw);
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(v));
    } catch {
      /* ignore */
    }
  }, [key, v]);
  return [v, setV];
}

/** Keyboard arrows for next/prev when the host element is hovered or focused. */
export function useArrowKeys(hostRef, onNext, onPrev) {
  useEffect(() => {
    const el = hostRef.current;
    if (!el) return undefined;
    let active = false;
    const enter = () => {
      active = true;
    };
    const leave = () => {
      active = false;
    };
    const key = (e) => {
      if (!active) return;
      if (e.key === "ArrowRight") {
        e.preventDefault();
        onNext();
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        onPrev();
      }
    };
    el.addEventListener("mouseenter", enter);
    el.addEventListener("mouseleave", leave);
    el.addEventListener("focusin", enter);
    el.addEventListener("focusout", leave);
    window.addEventListener("keydown", key);
    return () => {
      el.removeEventListener("mouseenter", enter);
      el.removeEventListener("mouseleave", leave);
      el.removeEventListener("focusin", enter);
      el.removeEventListener("focusout", leave);
      window.removeEventListener("keydown", key);
    };
  }, [hostRef, onNext, onPrev]);
}

/** Restore + persist scrollTop of an element, keyed per-step. */
export function useScrollRestore(ref, key) {
  useEffect(() => {
    const el = ref.current;
    if (!el || !key) return undefined;
    try {
      const saved = parseFloat(window.localStorage.getItem(key) || "0");
      if (!Number.isNaN(saved)) el.scrollTop = saved;
    } catch {
      /* ignore */
    }
    const onScroll = () => {
      try {
        window.localStorage.setItem(key, String(el.scrollTop));
      } catch {
        /* ignore */
      }
    };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [ref, key]);
}


// Re-export ref for convenience
export { useRef };
